import { ComponentProps, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ChevronDownIcon, ChevronUpIcon, HStack, IIconProps, Pressable, VStack } from 'native-base';

import { Text } from '@pimm/base';
import { SidebarMenuItem, useSidebarMenu } from '../hooks/sidebar-menu.hook';

type SidebarMenuProps = Omit<ReturnType<typeof useSidebarMenu>, 'setActiveItem'> & {
  setActiveItem: (item: SidebarMenuItem | undefined) => void;
  _container?: ComponentProps<typeof VStack>;
};

export const SidebarMenu = ({ _container, activeItem, items, setActiveItem }: SidebarMenuProps) => {
  const { t } = useTranslation();

  return (
    <VStack flex={1} py={3} w="1/3" maxWidth={240} borderRightWidth={1} {..._container}>
      {items.map(_item => {
        const firstChild = _item.children?.[0];
        const isHighighted = _item.name === activeItem?.name || _item.children?.some(_ => _.name === activeItem?.name);
        const isExpanded = !!firstChild && isHighighted;

        const icon = isExpanded ? ChevronUpIcon : ChevronDownIcon;

        return (
          <VStack key={_item.name} space={0}>
            <Pressable onPress={() => setActiveItem(firstChild ?? _item)}>
              <HStack
                px={3}
                py={2}
                alignItems="center"
                justifyContent="space-between"
                bg={isHighighted ? 'primary.50' : 'transparent'}
                borderLeftWidth={3}
                borderLeftColor={isHighighted ? 'primary.500' : 'transparent'}
              >
                <Box flex={1}>
                  <Text size="md" fontWeight={isHighighted ? 600 : 500} color={isHighighted ? 'primary.700' : 'gray.600'}>
                    {t(_item.label)}
                  </Text>
                </Box>

                {!!firstChild && createElement<IIconProps>(icon, { size: 3, color: 'gray.600' })}
              </HStack>
            </Pressable>

            {isExpanded && (
              <VStack py={1}>
                {_item.children?.map(_subItem => {
                  const isActive = _subItem.name === activeItem?.name;
                  return (
                    <Pressable key={_subItem.name} onPress={() => setActiveItem(_subItem)}>
                      <Box pl={6} pr={3} py={1.5}>
                        <Text size="md" fontWeight={isActive ? 600 : 400} color={isActive ? 'primary.700' : 'gray.500'}>
                          {t(_subItem.label)}
                        </Text>
                      </Box>
                    </Pressable>
                  );
                })}
              </VStack>
            )}
          </VStack>
        );
      })}
    </VStack>
  );
};
